import { useState } from "react"

import { OccupancyIcon } from "@minuthq/meatball-ui-icons"

import {
  MonitoringCard,
  StatusLeft,
} from "src/components/Homes/HomeDetails/Overview/MonitoringCard"
import { THome } from "src/data/homes/types/homeTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

import { MotionAutomationPopover } from "./MotionAutomationPopover"

/**
 * MotionAutomationCard
 * Card component for viewing and managing motion automation settings for a home.
 * Displays current status and allows users with permission to manage settings via a popover.
 * @example <MotionAutomationCard toggleAllowed={true} homeData={home} />
 */
export function MotionAutomationCard({
  toggleAllowed,
  homeData,
}: {
  toggleAllowed?: boolean
  homeData: THome
}) {
  const { t, langKeys } = useTranslate()
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const tooltipText = toggleAllowed ? "" : t(langKeys.not_enough_permissions)
  const isEnabled = homeData.motion_automation?.mode === "enabled"

  function handleClick(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    if (toggleAllowed) {
      setOpen(true)
      setAnchorEl(event.currentTarget)
    }
  }

  function handleClose() {
    setOpen(false)
    setAnchorEl(null)
  }

  return (
    <>
      <MonitoringCard
        titleSlots={[
          <StatusLeft
            key="motion-status"
            icon={<OccupancyIcon width={32} height={32} />}
            title={t(langKeys.motion_automation_title)}
            statusContents={isEnabled ? t(langKeys.on) : t(langKeys.off)}
            isOn={true}
          />,
          <MTooltip
            title={tooltipText}
            key="motion-automation-tooltip"
            placement={"top"}
          >
            <div>
              <MButton
                variant="subtle"
                size="small"
                disabled={!toggleAllowed}
                onClick={handleClick}
              >
                {t(langKeys.manage)}
              </MButton>
            </div>
          </MTooltip>,
        ]}
        cardFooter={undefined}
      />
      <MotionAutomationPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        state={homeData.motion_automation?.mode}
        homeId={homeData.home_id}
      />
    </>
  )
}
